import $ from 'jquery';

const fields = new Map();
const paymentMethods = {};

const SHIPPING_OPTION_REGEX = /^(?<index>\d+):(?<id>.+)$/;

export const getSetting = (key, defaultValue = null) => {
    if (typeof window.wcStripeSettings !== 'undefined') {
        return window.wcStripeSettings[key] || defaultValue;
    }
    return defaultValue;
}

export const extractShippingMethod = value => {
    const match = value.match(SHIPPING_OPTION_REGEX);
    if (match) {
        return {
            [match.groups.index]: match.groups.id
        }
    }
    return null;
}

/**
 * Check if a field is required
 * @param {string} fieldName - The field name (e.g., 'billing_email', 'billing_first_name')
 * @returns {boolean} True if the field is required
 */
export const isFieldRequired = (fieldName) => {
    const requiredFields = getSetting('requiredFields') || [];
    return requiredFields.includes(fieldName);
};

export const isFieldValid = key => {
    let value = getFieldValue(key);
    value = value?.trim();
    return !!value;
}

export const getRestRoute = (key) => {
    return getSetting('restRoutes')?.[key]?.url || null;
}

/**
 * Stores the value in the fields Map with the provided key. If there is DOM element
 * with a name that matches the key then its value is also updated.
 * @param key
 * @param value
 * @param prefix
 */
export const setFieldValue = (key, value, prefix = null) => {
    if (prefix) {
        key = `${prefix}_${key}`;
    }
    fields.set(key, value);

    if ($(`[name="${key}"]`).length) {
        $(`[name="${key}"]`).val(value);
    }
}

export const getFieldValue = (key, prefix = null) => {
    if (prefix) {
        key = `${prefix}_${key}`;
    }
    return fields.get(key);
}

/**
 * Strips <script> elements and on*-prefixed attributes from an HTML string, leaving everything
 * else (e.g. WooCommerce's own <strong>-wrapped field labels in required-field validation
 * messages - see WC_Checkout::validate_posted_data()) intact. Parses into a detached document
 * (document.implementation.createHTMLDocument) so nothing executes while sanitizing - same
 * approach as @wordpress/dom's safeHTML(), reimplemented here to avoid a new dependency.
 *
 * @param {string} html
 * @returns {string}
 */
export const sanitizeHtml = (html) => {
    const {body} = document.implementation.createHTMLDocument('');
    body.innerHTML = html;
    const elements = body.getElementsByTagName('*');
    let i = elements.length;
    while (i--) {
        const element = elements[i];
        if (element.tagName === 'SCRIPT') {
            element.remove();
        } else {
            let j = element.attributes.length;
            while (j--) {
                const {name} = element.attributes[j];
                if (name.indexOf('on') === 0) {
                    element.removeAttribute(name);
                }
            }
        }
    }
    return body.innerHTML;
}

/**
 * Get user-friendly error message
 *
 * Converts Stripe error objects or raw error messages into localized,
 * user-friendly messages. Maps error codes to custom messages from settings.
 *
 * @param {string|Object|Array} error - Error from Stripe or server
 * @returns {string|Array} User-friendly error message(s)
 */
export const getErrorMessage = error => {
    // Handle null/undefined
    if (!error) {
        return getSetting('errorMessages')?.['generic'];
    }

    const messages = getSetting('errorMessages') || {};

    // Handle array of errors - return as-is
    if (Array.isArray(error)) {
        return error;
    }

    // Handle string errors - return as-is
    if (typeof error === 'string') {
        return error;
    }

    // Handle error objects
    if (typeof error === 'object') {
        // Detect special error cases from message content
        if (error.message) {
            if (error.message.indexOf('server_side_confirmation_beta=v1') > -1) {
                error.code = 'server_side_confirmation_beta';
            } else if (error.message.indexOf('params.payment_method_data.billing_details.phone') > -1) {
                error.code = 'phone_required';
            } else if (error.message.indexOf('fields.billing_details.phone') > -1) {
                error.code = 'phone_required';
            }
        }

        // Try to get custom message for error code
        if (error.code && messages[error.code]) {
            return messages[error.code];
        }

        // Fallback to error.message if available
        if (error.message) {
            return error.message;
        }

        // Last resort: stringify the error object
        return JSON.stringify(error);
    }

    // Unknown error type - return generic message
    return messages['generic'] || 'An error occurred. Please try again.';
}

/**
 * Convert the fields map to a JSON object.
 * @returns {{}}
 */
export const fieldsToJson = () => {
    const json = {};
    fields.forEach((value, key) => {
        json[key] = value;
    });
    return json;
}

/**
 * Returns true if the intent requires a next action.
 * @param url
 * @returns {null}
 */
export const isNextActionRequired = (url) => {
    if (typeof url !== 'string') {
        return false;
    }
    let match = url.match(/#response=(.+)/);
    let args = null;
    if (match) {
        args = JSON.parse(window.atob(decodeURIComponent(match[1])));
    }
    return args;
}

export const parseOrderReviewParams = (search) => {
    if (typeof search !== 'string') {
        return false;
    }
    let match = search.match(/_stripe_order_review=(.+)/);
    let args = null;
    if (match) {
        args = JSON.parse(window.atob(decodeURIComponent(match[1])));
    }
    return args;
}

export const getPage = () => getSetting('page');

/**
 * Parse a formatted price string into a float using the store's currency settings.
 * Strips anything that isn't a digit or a known separator, so currency symbols
 * (including HTML-encoded variants) are automatically removed.
 *
 * @param {string} text - Formatted price string e.g. "$1,299.50" or "1.299,50 €"
 * @returns {number|null} Parsed price or null if parsing fails
 */
export const parsePrice = (text) => {
    const {thousandSeparator = ',', decimalSeparator = '.'} = getSetting('currency') || {};

    // Escape characters with special meaning inside a regex character class
    const escapeForClass = (s) => s.replace(/[\]\\^-]/g, '\\$&');
    const keep = [escapeForClass(thousandSeparator), escapeForClass(decimalSeparator)].join('');

    // Strip everything that isn't a digit or one of the separators
    let normalized = text.replace(new RegExp(`[^\\d${keep}]`, 'g'), '');

    // Remove thousand separators
    if (thousandSeparator) {
        normalized = normalized.split(thousandSeparator).join('');
    }

    // Normalize decimal separator to '.'
    if (decimalSeparator !== '.') {
        normalized = normalized.replace(decimalSeparator, '.');
    }

    const price = parseFloat(normalized);
    return isNaN(price) ? null : price;
};

/**
 * Convert a price to its minor unit (cents) representation.
 * Uses Math.round to avoid floating point precision issues e.g. 1.23 * 100 = 122.999...
 *
 * @param {number} price - Price in major unit e.g. 12.99
 * @returns {number} Price in minor unit e.g. 1299
 */
export const toCents = (price) => {
    const {minorUnit = 2} = getSetting('currency') || {};
    return Math.round(price * Math.pow(10, minorUnit));
};

export const convertFormToFields = ($form, exclude = []) => {
    return $form.serializeArray().reduce((obj, item) => {
        if (exclude.includes(item.name)) return obj;

        const keys = [];
        item.name.replace(/([^\[\]]*)/g, (_, key) => {
            if (key !== '') {
                keys.push(key)
            }
        });

        const isArray = item.name.endsWith('[]');

        keys.reduce((current, key, i) => {
            if (i === keys.length - 1) {
                if (isArray) {
                    current[key] = current[key] ?? [];
                    current[key].push(item.value);
                } else {
                    current[key] = item.value;
                }
            } else {
                current[key] = current[key] ?? {};
            }
            return current[key];
        }, obj);

        return obj;
    }, {});
};

export const registerPaymentMethod = paymentMethod => {
    paymentMethods[paymentMethod.id] = paymentMethod;
}

export const hasPaymentMethod = id => !!paymentMethods[id];

export {
    paymentMethods
}